import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Building2, Users, ChevronDown } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import GlossaryLink from './common/GlossaryLink';
import ExportButton from './common/ExportButton';
import { exportElementAsPNG, generateExportFilename } from '../utils/exportUtils';

/**
 * Hospital Distribution Chart Component
 * Styled to match RiskTable — collapsible accordion with same header pattern
 * Now includes per-hospital PFS/OS survival percentages
 */
const HospitalDistributionChart = ({
  hospitalData = null,
  totalPatients = 0,
  title = "Hospital Distribution",
  subtitle = "Patient distribution across treatment centers",
  userRole = null,
  indication = 'NHL',
  analysisType = 'PFS',
  timeline = '12 Month',
  filters = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [barsVisible, setBarsVisible] = useState(false);
  const [hospitalSurvival, setHospitalSurvival] = useState({});
  const [survivalLoading, setSurvivalLoading] = useState(false);
  const [sortMode, setSortMode] = useState('count'); // 'count' or 'response'
  const hospitalRef = useRef(null);

  const handleExportPNG = async () => {
    if (!hospitalRef.current) return;
    const filename = generateExportFilename('Hospital_Distribution', { indication });
    await exportElementAsPNG(hospitalRef.current, filename, { style: { padding: '16px' } });
  };

  // Superadmin & Stakeholder see real names. Sales & Physician see anonymous names.
  const shouldAnonymize = userRole === 'sales' || userRole === 'physician';

  // Trigger bar animation shortly after panel opens
  useEffect(() => {
    if (isOpen) {
      setBarsVisible(false);
      const t = setTimeout(() => setBarsVisible(true), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Pre-fetch per-hospital survival data as soon as hospital data is available
  // (not waiting for accordion to open — data loads in background)
  useEffect(() => {
    if (!hospitalData || Object.keys(hospitalData).length === 0) return;

    const hospitalNames = Object.keys(hospitalData);
    // Skip if already fetched for these hospitals
    const allFetched = hospitalNames.every(h => hospitalSurvival[h] !== undefined);
    if (allFetched) return;

    const timelineMonths = parseInt(timeline) || 12;
    const indicationCode = indication === 'NHL' ? 'nhl' : 'all';
    const analysisCode = analysisType.toLowerCase() === 'os' ? 'os' : 'pfs';

    setSurvivalLoading(true);

    const fetchHospitalSurvival = async () => {
      const results = {};

      await Promise.all(
        hospitalNames.map(async (hospitalName) => {
          try {
            const payload = {
              indication: indicationCode,
              analysis_type: analysisCode,
              lines_of_failure: 'all',
              active_level: 'institute',
              selected_institute: hospitalName,
              apheresis_cutoff: filters?.apheresisCutoff || null,
              last_date_of_followup_cutoff: filters?.lastDateOfFollowup || null,
              timeline_months: timelineMonths,
            };

            const resp = await fetch(API_ENDPOINTS.survivalAnalysis, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              results[hospitalName] = { pfs: null, os: null };
              return;
            }

            const data = await resp.json();
            const km = data.overall_km_object || data.overall_km;

            if (km && km.y && km.y.length > timelineMonths) {
              const survivalVal = Math.round(km.y[timelineMonths] * 100);
              results[hospitalName] = {
                pfs: analysisCode === 'pfs' ? survivalVal : null,
                os: analysisCode === 'os' ? survivalVal : null,
              };
            } else {
              results[hospitalName] = { pfs: null, os: null };
            }
          } catch (err) {
            console.warn(`Failed to fetch survival for ${hospitalName}:`, err.message);
            results[hospitalName] = { pfs: null, os: null };
          }
        })
      );

      setHospitalSurvival(prev => ({ ...prev, ...results }));
      setSurvivalLoading(false);
    };

    fetchHospitalSurvival();
  }, [hospitalData, indication, analysisType, timeline, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = useMemo(() => {
    if (!hospitalData || Object.keys(hospitalData).length === 0) return null;

    // Backend already returns the correct top hospitals — show all of them as-is
    let bars = Object.entries(hospitalData)
      .map(([name, count]) => ({
        name: name || 'Unknown',
        count,
        rawName: name,
      }));

    // Sort based on current mode
    if (sortMode === 'response') {
      // Sort by response rate (PFS/OS %) descending — hospitals without data go to bottom
      bars = bars.sort((a, b) => {
        const aRate = hospitalSurvival[a.rawName]?.pfs ?? hospitalSurvival[a.rawName]?.os ?? -1;
        const bRate = hospitalSurvival[b.rawName]?.pfs ?? hospitalSurvival[b.rawName]?.os ?? -1;
        return bRate - aRate;
      });
    } else {
      // Sort by patient count descending (default)
      bars = bars.sort((a, b) => b.count - a.count);
    }

    // Take top 5 and anonymize if needed
    bars = bars.slice(0, 5).map((bar, idx) => ({
      ...bar,
      name: shouldAnonymize ? `Hospital ${idx + 1}` : bar.name,
    }));

    const maxCount = Math.max(...bars.map(b => b.count));
    return bars.map((bar, idx) => ({
      ...bar,
      percentage: totalPatients > 0 ? (bar.count / totalPatients) * 100 : 0,
      barWidth: maxCount > 0 ? (bar.count / maxCount) * 100 : 0,
      color: BAR_COLORS[idx % BAR_COLORS.length],
    }));
  }, [hospitalData, totalPatients, shouldAnonymize, sortMode, hospitalSurvival]);

  if (!chartData || chartData.length === 0) return null;

  const survivalLabel = analysisType.toLowerCase() === 'os' ? 'OS' : 'PFS';

  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden relative" ref={hospitalRef}>
      {/* Export Button — only visible when accordion is open */}
      {isOpen && (
        <ExportButton
          onExportPNG={handleExportPNG}
          userRole={userRole}
          position="top-right"
          className="top-2.5 right-2.5"
        />
      )}

      {/* Header - same pattern as RiskTable */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full bg-gradient-to-r from-slate-50 to-slate-100 px-4 py-3 text-left focus:outline-none"
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                Hospital Distribution <GlossaryLink section="hospital" tooltip="Distribution of patients across hospitals. Toggle between sorting by patient count or response rate (PFS/OS %)." />
              </h3>
            </div>
            <p className="text-xs text-slate-600 mt-1">{subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 px-2.5 py-1 rounded-lg">
              <Users className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-bold text-slate-700">{totalPatients}</span>
              <span className="text-xs text-slate-400">Total</span>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {/* Collapsible Content */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isOpen ? '2000px' : '0px', opacity: isOpen ? 1 : 0 }}
      >
        {/* Sort Toggle */}
        <div className="px-4 pt-3 pb-1 flex items-center gap-2" data-hide-on-export="true">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Sort by:</span>
          <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
            <button
              onClick={() => setSortMode('count')}
              className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
                sortMode === 'count'
                  ? 'bg-white text-slate-800 shadow-sm border border-slate-200/50'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Patient Count
            </button>
            <button
              onClick={() => setSortMode('response')}
              className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
                sortMode === 'response'
                  ? 'bg-white text-slate-800 shadow-sm border border-slate-200/50'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Response Rate
            </button>
          </div>
        </div>

        <div className="px-4 py-4 space-y-4">
          {chartData.map((bar, idx) => {
            const survival = hospitalSurvival[bar.rawName];
            const survivalValue = survival ? (survival.pfs ?? survival.os) : null;

            return (
              <div key={bar.rawName} className="group">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-slate-700 truncate" title={shouldAnonymize ? undefined : bar.name}>
                    {bar.name}
                  </span>
                  <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                    {survivalValue !== null && (
                      <span className="text-sm font-bold text-gray-900">{survivalLabel}: {survivalValue}%</span>
                    )}
                    {survivalLoading && survivalValue === null && survivalValue === undefined && (
                      <span className="text-xs text-slate-400">...</span>
                    )}
                  </div>
                </div>
                <div className="relative h-8 bg-slate-100 rounded-lg">
                  <div
                    className="absolute inset-y-0 left-0 rounded-lg transition-all duration-700 ease-out overflow-hidden"
                    style={{
                      width: barsVisible
                        ? `${sortMode === 'response' && survivalValue !== null ? survivalValue : bar.barWidth}%`
                        : '0%',
                      transitionDelay: `${idx * 0.1}s`,
                      backgroundColor: bar.color,
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
                  </div>
                  <div className="absolute inset-0 flex items-center px-3 z-10">
                    <span className="text-xs font-bold whitespace-nowrap text-white drop-shadow-sm">
                      {sortMode === 'response'
                        ? `${survivalLabel}: ${survivalValue !== null ? `${survivalValue}%` : '—'}`
                        : `#${bar.count} (${bar.percentage.toFixed(0)}%)`
                      }
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
          <p className="text-xs text-slate-500">
            {(() => {
              const shownTotal = chartData.reduce((sum, b) => sum + b.count, 0);
              const remaining = totalPatients - shownTotal;
              const sortLabel = sortMode === 'response' ? 'response rate' : 'patient count';
              return remaining > 0
                ? <span>Showing top <strong className="text-slate-700">{chartData.length}</strong> treatment centers by {sortLabel}. <strong className="text-slate-700">{remaining}</strong> additional patient{remaining > 1 ? 's' : ''} from other centers not shown.</span>
                : <span>Showing top <strong className="text-slate-700">{chartData.length}</strong> treatment centers by {sortLabel}.</span>;
            })()}
          </p>
        </div>
      </div>
    </div>
  );
};

const BAR_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

export default HospitalDistributionChart;
